import clsx from 'clsx';
import React from 'react';

import useEventTracking from '@hooks/useEventTracking';
import { Column } from '@tanstack/react-table';
import { ClickTarget, click } from '@tracking';

interface DataTableColumnHeaderProps<TData, TValue> extends React.HTMLAttributes<HTMLDivElement> {
  column: Column<TData, TValue>;
  title: string;
}

const SORT_ASC = 'asc';
const SORT_DESC = 'desc';
const sharedArrowClassNames = 'absolute right-8 mt-2 font-[FontAwesome] text-button-xs';

const getSortIcon = <TData, TValue>(column: Column<TData, TValue>) => {
  if (column.getIsSorted() === SORT_DESC) {
    return <span className={clsx(sharedArrowClassNames, 'text-primary-magenta')}>&#xf0dd;</span>;
  } else if (column.getIsSorted() === SORT_ASC) {
    return <span className={clsx(sharedArrowClassNames, 'text-primary-magenta')}>&#xf0de;</span>;
  } else {
    return <span className={clsx(sharedArrowClassNames, 'text-gray-400')}>&#xf0dc;</span>;
  }
};

export const DataTableColumnHeader = <TData, TValue>({
  column,
  title,
}: DataTableColumnHeaderProps<TData, TValue>) => {
  const { trackEvent } = useEventTracking();

  if (!column.getCanSort()) {
    return <div>{title}</div>;
  }

  return (
    <div
      className="relative flex items-center space-x-2"
      onClick={() => column.toggleSorting()}
      onClickCapture={() =>
        trackEvent(click({ clickTarget: ClickTarget.SortColumn, clickText: title }))
      }
    >
      <span onDoubleClick={() => column.clearSorting()}>
        {title}
        {getSortIcon(column)}
      </span>
    </div>
  );
};
